// src/pages/PdiPrintPage.js
// PDI Booklet — matches physical SNA PDI booklet format
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../scripts/api";
const darkLogo = "/SNA.png";

const MAROON = "#7B0000";
const YELLOW = "#FFFF00";
const ADDR   = "7530 114 ave SE Calgary, AB T2C 4T3";
const PHONE  = "Ph: (403) 263-9776   Fax: (403) 263-9717   Toll Free: (866) 733-9776";

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toISOString().slice(0, 10);
}

const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  @media print {
    @page { size: letter portrait; margin: 12mm 12mm; }
    body  { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print   { display: none !important; }
    .page-break { page-break-before: always; break-before: page; }
    .avoid-break { page-break-inside: avoid; break-inside: avoid; }
  }

  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9.5pt;
    color: #000;
    background: #d0d0d0;
  }

  .booklet {
    max-width: 820px;
    margin: 0 auto;
    background: #fff;
  }
  @media screen {
    .booklet { box-shadow: 0 4px 32px rgba(0,0,0,.3); margin: 28px auto; }
  }

  /* ── print button ── */
  .print-fab {
    position: fixed; bottom: 24px; right: 24px;
    background: ${MAROON}; color: #fff;
    border: none; border-radius: 6px;
    padding: 12px 26px; font-size: 11pt; font-weight: 700;
    cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,.3); z-index: 999;
  }
  .print-fab:hover { opacity: 0.85; }

  /* ── cover ── */
  .cover {
    padding: 28px 36px 36px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  @media print {
    .cover { min-height: 0; height: 100vh; padding: 18px 24px 28px; }
  }

  .cover-checkboxes {
    position: absolute;
    top: 28px; right: 36px;
    text-align: right;
    font-size: 8pt;
  }
  .cover-checkboxes table { border-collapse: collapse; margin-left: auto; }
  .cover-checkboxes td { padding: 2px 4px; font-size: 8pt; }
  .cover-checkboxes .cb { width: 48px; height: 16px; border: 1.5px solid #000; display: inline-block; }

  .cover-logo-wrap {
    display: flex;
    justify-content: center;
    margin: 36px 0 24px;
  }
  .cover-logo { height: 80px; object-fit: contain; }

  .cover-title {
    text-align: center;
    font-size: 52pt;
    font-weight: 900;
    letter-spacing: 2px;
    line-height: 1;
    margin-bottom: 4px;
  }
  .cover-title-rule {
    border: none;
    border-top: 4px double #000;
    margin: 6px auto;
    width: 80%;
  }
  .cover-subtitle {
    text-align: center;
    font-size: 13pt;
    font-weight: 700;
    letter-spacing: 2px;
    margin-bottom: 32px;
  }

  .cover-fields {
    width: 80%;
    margin: 0 auto 32px;
  }
  .cover-field-row {
    display: flex;
    align-items: flex-end;
    margin-bottom: 18px;
    gap: 12px;
  }
  .cover-field-label {
    font-weight: 700;
    font-size: 11pt;
    white-space: nowrap;
    min-width: 220px;
    text-align: right;
  }
  .cover-field-value {
    flex: 1;
    border-bottom: 1.5px solid #000;
    font-size: 11pt;
    padding-bottom: 2px;
    min-height: 20px;
  }

  .cover-dnr {
    text-align: center;
    font-weight: 700;
    font-size: 12pt;
    margin-top: auto;
    padding-top: 32px;
  }

  .cover-page-label {
    text-align: center;
    font-size: 8pt;
    color: #555;
    margin-top: 16px;
  }

  /* ── interior page header (maroon banner + logo) ── */
  .page-wrap {
    padding: 0 0 20px;
  }
  .interior-banner {
    background: ${MAROON};
    color: #fff;
    text-align: center;
    font-size: 14pt;
    font-weight: 700;
    letter-spacing: 2px;
    padding: 7px 0;
  }
  .interior-logo-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 8px 16px 6px;
    border-bottom: 1px solid #ccc;
  }
  .interior-logo { height: 44px; object-fit: contain; }
  .interior-addr { font-size: 8pt; line-height: 1.5; color: #222; }

  .machine-info-block {
    padding: 6px 16px;
    border-bottom: 1.5px solid #000;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px 24px;
    font-size: 8.5pt;
  }
  .machine-info-block .mi-row { display: flex; gap: 6px; }
  .machine-info-block .mi-label { font-weight: 700; white-space: nowrap; }
  .machine-info-block .mi-val { border-bottom: 1px solid #000; flex: 1; min-width: 80px; }

  /* ── content padding ── */
  .content-pad { padding: 0 16px; }

  /* ── section banner (yellow) ── */
  .section-banner {
    background: ${MAROON};
    color: #fff;
    text-align: center;
    font-size: 11pt;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 0;
    margin: 10px 0 0;
  }

  /* ── deficiency table ── */
  .def-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8pt;
    margin-top: 0;
  }
  .def-table th {
    background: #FFFF00;
    color: #000;
    border: 1px solid #999;
    padding: 5px 6px;
    font-weight: 700;
    font-size: 7.5pt;
    text-align: center;
    vertical-align: middle;
  }
  .def-table th.th-serial {
    min-width: 90px;
    font-size: 10pt;
    font-weight: 900;
  }
  .def-table td {
    border: 1px solid #aaa;
    padding: 4px 6px;
    vertical-align: middle;
    min-height: 18px;
  }
  .def-table .td-num { text-align: center; font-weight: 700; width: 28px; }
  .def-table tr:nth-child(even) td { background: #f9f9f9; }

  /* ── checklist table ── */
  .check-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8pt;
    margin-top: 0;
  }
  .check-table th {
    background: ${YELLOW};
    color: #000;
    border: 1px solid #999;
    padding: 4px 5px;
    font-weight: 700;
    font-size: 7.5pt;
    text-align: center;
    vertical-align: middle;
  }
  .check-table th.th-rotate {
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    white-space: nowrap;
    padding: 6px 3px;
    width: 22px;
    min-width: 22px;
    max-width: 22px;
  }
  .check-table th.th-item   { width: 36px; }
  .check-table th.th-verify { width: 130px; }
  .check-table th.th-insp   { /* flexible */ }
  .check-table th.th-ok, .check-table th.th-notok, .check-table th.th-rep {
    width: 28px; min-width: 28px;
  }
  .check-table th.th-comments { min-width: 140px; }

  .check-table td {
    border: 1px solid #aaa;
    padding: 4px 6px;
    vertical-align: middle;
  }
  .check-table .td-num  { text-align: center; font-weight: 700; }
  .check-table .td-chk  { text-align: center; font-size: 11pt; }
  .check-table .td-comments { font-size: 7.5pt; color: #333; }

  .check-table tr.section-row td {
    background: ${YELLOW};
    font-weight: 700;
    font-size: 8.5pt;
    letter-spacing: 0.5px;
    padding: 4px 8px;
  }

  .check-table tr:nth-child(even):not(.section-row) td { background: #fafafa; }

  /* ── page footer ── */
  .interior-footer {
    padding: 6px 16px 0;
    display: flex;
    justify-content: space-between;
    font-size: 7.5pt;
    color: #555;
    border-top: 1px solid #ddd;
    margin-top: 10px;
  }
`;

// ── Shared interior page header ────────────────────────────────────────────────
function InteriorHeader({ machineType, model, serial, endUser }) {
  return (
    <>
      <div className="interior-banner">PDI BOOKLET</div>
      <div className="interior-logo-row">
        <img src={darkLogo} alt="SNA" className="interior-logo" />
        <div className="interior-addr">
          {ADDR}<br />{PHONE}
        </div>
      </div>
      <div className="machine-info-block">
        <div className="mi-row">
          <span className="mi-label">Machine type:</span>
          <span className="mi-val">{machineType}</span>
        </div>
        <div className="mi-row">
          <span className="mi-label">Model</span>
          <span className="mi-val">{model}</span>
        </div>
        <div className="mi-row">
          <span className="mi-label">Serial Number:</span>
          <span className="mi-val">{serial}</span>
        </div>
        <div className="mi-row">
          <span className="mi-label">End user unit #:</span>
          <span className="mi-val">{endUser}</span>
        </div>
      </div>
    </>
  );
}

// ── Checklist table header row ─────────────────────────────────────────────────
function ChecklistThead() {
  return (
    <thead>
      <tr>
        <th className="th-item">ITEM</th>
        <th className="th-verify">ITEM TO VERIFY</th>
        <th className="th-insp">REQUIRED INSPECTION</th>
        <th className="th-ok th-rotate">OK</th>
        <th className="th-notok th-rotate">NOT OK</th>
        <th className="th-rep th-rotate">REPAIRED</th>
        <th className="th-comments">COMPLETED DATE &amp; COMMENTS</th>
        <th className="th-rotate">INITIALS</th>
      </tr>
    </thead>
  );
}

// ── component ──────────────────────────────────────────────────────────────────
export default function PdiPrintPage() {
  const { id }            = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/pdi/sessions/${id}/full-report`)
      .then(({ data: d }) => setData(d))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, [id]);

  useEffect(() => {
    if (data) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (error) return (
    <div style={{ padding: 40, fontFamily: "sans-serif", color: "#dc2626" }}>
      Error loading PDI report: {error}
    </div>
  );
  if (!data) return (
    <div style={{ padding: 40, fontFamily: "sans-serif", color: "#555" }}>
      Loading PDI report…
    </div>
  );

  const { session, template, responses, deficiencies, inventory, warranty } = data;
  const unit = inventory || {};
  const warr = warranty || {};

  // Response lookup by step_id
  const respMap = {};
  for (const r of (responses || [])) respMap[r.step_id] = r;

  // Derived values
  const machineType  = template?.vendor || unit.body_make_model || unit.make_model || "—";
  const modelStr     = unit.chassis_make_model || unit.body_make_model || "—";
  const serialStr    = unit.body_vin || unit.chassis_vin || "—";
  const endUserStr   = unit.customer || "—";
  const salesWO      = unit.rowo || unit.stock_po || "—";
  const pdiDate      = fmtDate(session.completed_at || session.started_at);
  const templateName = [template?.vendor, template?.title].filter(Boolean).join(" — ") || "Pre-Delivery Inspection";

  // Deficiencies split into pages of 20 rows each
  const defs       = deficiencies || [];
  const DEF_ROWS   = 20;
  const defPages   = [];
  for (let i = 0; i < Math.max(defs.length, DEF_ROWS); i += DEF_ROWS) {
    defPages.push(defs.slice(i, i + DEF_ROWS));
  }

  // All sections + steps
  const sections = template?.sections || [];
  const allSteps = sections.flatMap(s => s.steps || []);
  const totalSteps = allSteps.length;

  // Page counter helper
  let pageNum = 1;
  const nextPage = () => ++pageNum;

  return (
    <>
      <style>{CSS}</style>

      <button className="print-fab no-print" onClick={() => window.print()}>
        Print / Save PDF
      </button>

      <div className="booklet">

        {/* ════════════════════════════════════════
            PAGE 1 — COVER
        ════════════════════════════════════════ */}
        <div className="cover">

          {/* Top-right checkboxes */}
          <div className="cover-checkboxes">
            <table>
              <tbody>
                <tr>
                  <td>SCANNED (Eng dept)</td>
                  <td><span className="cb" /></td>
                </tr>
                <tr>
                  <td>IN SERVICE REGISTRATION (warranty dept)</td>
                  <td><span className="cb" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Logo */}
          <div className="cover-logo-wrap">
            <img src={darkLogo} alt="Superior North America" className="cover-logo" />
          </div>

          {/* Title */}
          <div className="cover-title">PDI BOOKLET</div>
          <hr className="cover-title-rule" />
          <div className="cover-subtitle">{templateName.toUpperCase()}</div>

          {/* Fields */}
          <div className="cover-fields">
            <div className="cover-field-row">
              <div className="cover-field-label">Machine Serial Number:</div>
              <div className="cover-field-value">{serialStr !== "—" ? serialStr : ""}</div>
            </div>
            <div className="cover-field-row">
              <div className="cover-field-label">End User Name &amp; Unit #:</div>
              <div className="cover-field-value">{endUserStr !== "—" ? endUserStr : ""}</div>
            </div>
            <div className="cover-field-row">
              <div className="cover-field-label">Date:</div>
              <div className="cover-field-value">{pdiDate}</div>
            </div>
            <div className="cover-field-row">
              <div className="cover-field-label">Sales Work Order:</div>
              <div className="cover-field-value">{salesWO !== "—" ? salesWO : ""}</div>
            </div>
            <div className="cover-field-row">
              <div className="cover-field-label">Warranty Work Order:</div>
              <div className="cover-field-value">{session.warranty_wo || ""}</div>
            </div>
          </div>

          <div className="cover-dnr">DO NOT REMOVE FROM VEHICLE</div>
          <div className="cover-page-label">COVER</div>
        </div>

        {/* ════════════════════════════════════════
            DEFICIENCY PAGES
        ════════════════════════════════════════ */}
        {defPages.map((chunk, pi) => {
          const startNum = pi * DEF_ROWS + 1;
          const endNum   = startNum + DEF_ROWS - 1;
          const pgLabel  = defPages.length > 1
            ? `DEFICIENCIES (P ${pi + 1}/${defPages.length})`
            : "DEFICIENCIES";
          const pg = nextPage();
          return (
            <div key={pi} className="page-break page-wrap avoid-break">
              <InteriorHeader
                machineType={machineType}
                model={modelStr}
                serial={serialStr}
                endUser={endUserStr}
              />
              <div className="content-pad">
                <div className="section-banner">{pgLabel}</div>
                <table className="def-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}>ITEM</th>
                      <th>DEFECT DESCRIPTION</th>
                      <th className="th-serial">{serialStr}</th>
                      <th style={{ width: 72 }}>DATE NOTED</th>
                      <th style={{ width: 90 }}>SPOTTED BY<br />(Mechanic name &amp; Co.)</th>
                      <th style={{ width: 72 }}>RESOLVED DATE</th>
                      <th style={{ width: 90 }}>RESOLVED BY</th>
                      <th style={{ width: 48 }}>LABOR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: DEF_ROWS }).map((_, ri) => {
                      const def = chunk[ri];
                      const num = startNum + ri;
                      return (
                        <tr key={num}>
                          <td className="td-num">{num}</td>
                          <td>{def?.description || ""}</td>
                          <td>{def ? (def.step_title || "") : ""}</td>
                          <td>{def ? fmtDate(def.created_at) : ""}</td>
                          <td>{def?.spotted_by || ""}</td>
                          <td>{def ? fmtDate(def.resolved_at) : ""}</td>
                          <td>{def?.resolved_by || ""}</td>
                          <td></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 6, fontSize: "7.5pt", color: "#555" }}>
                  {pi === defPages.length - 1
                    ? "* Try to figure out to the best of your knowledge, who is responsible for the defect and put it above."
                    : `Body Deficiencies, Page ${pi + 2} if needed`}
                </div>
              </div>
              <div className="interior-footer">
                <span></span>
                <span>Page {pg} of {1 + defPages.length + sections.length}</span>
              </div>
            </div>
          );
        })}

        {/* ════════════════════════════════════════
            CHECKLIST PAGES — one page per section
        ════════════════════════════════════════ */}
        {sections.map((sec, si) => {
          const steps = sec.steps || [];
          const pg = nextPage();
          return (
            <div key={si} className="page-break page-wrap">
              <InteriorHeader
                machineType={machineType}
                model={modelStr}
                serial={serialStr}
                endUser={endUserStr}
              />
              <div className="content-pad">
                <div className="section-banner">SNA SPECIFIC PRE-DELIVERY INSPECTION LIST</div>
                <table className="check-table">
                  <ChecklistThead />
                  <tbody>
                    {/* Section label row */}
                    <tr className="section-row">
                      <td colSpan={8}>{sec.title || sec.name || `Section ${si + 1}`}</td>
                    </tr>
                    {steps.map((step, idx) => {
                      const resp   = respMap[step.id];
                      const status = resp?.status || "";
                      const isOk       = status === "ok";
                      const isNotOk    = status === "not_ok";
                      const isRepaired = status === "repaired";
                      const commentParts = [
                        resp?.updated_at ? fmtDate(resp.updated_at) : "",
                        resp?.comment || "",
                      ].filter(Boolean);
                      return (
                        <tr key={step.id || idx} className="avoid-break">
                          <td className="td-num">{step.order_index ?? idx + 1}</td>
                          <td>{step.item || step.title || step.name || ""}</td>
                          <td>{step.instruction || step.description || ""}</td>
                          <td className="td-chk">{isOk       ? "✓" : ""}</td>
                          <td className="td-chk">{isNotOk    ? "✓" : ""}</td>
                          <td className="td-chk">{isRepaired ? "✓" : ""}</td>
                          <td className="td-comments">{commentParts.join("  ·  ") || ""}</td>
                          <td className="td-chk" style={{ fontSize: "7pt" }}>{resp?.tech_name || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="interior-footer">
                <span></span>
                <span>Page {pg} of {1 + defPages.length + sections.length + 1}</span>
              </div>
            </div>
          );
        })}

        {/* ════════════════════════════════════════
            FINAL PAGE — WARRANTY REGISTRATION
        ════════════════════════════════════════ */}
        <div className="page-break page-wrap">
          <div className="interior-banner">PDI BOOKLET</div>
          <div className="interior-logo-row">
            <img src={darkLogo} alt="SNA" className="interior-logo" />
            <div className="interior-addr">{ADDR}<br />{PHONE}</div>
          </div>

          <div className="content-pad">
            <div className="section-banner" style={{ marginTop: 8 }}>WARRANTY / IN-SERVICE REGISTRATION</div>

            <div style={{ padding: "10px 0 4px", fontSize: "8pt", color: "#555" }}>
              To be completed by the warranty department once the unit has been delivered and put in service.
            </div>

            {/* Two-column unit + customer info */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: "8.5pt" }}>
              <tbody>
                <tr>
                  <td style={{ width: "50%", verticalAlign: "top", paddingRight: 12 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        {[
                          ["Machine Type",      machineType],
                          ["Model",             modelStr],
                          ["Serial Number",     serialStr],
                          ["Engine Serial",     warr.engine_serial || ""],
                          ["Hour Meter",        warr.hour_meter || ""],
                          ["Sales Work Order",  salesWO !== "—" ? salesWO : ""],
                          ["Warranty Work Order", warr.warranty_wo || ""],
                          ["PDI Completed",     fmtDate(session.completed_at)],
                          ["In-Service Date",   warr.in_service_date || ""],
                          ["Warranty Start",    warr.warranty_start_date || ""],
                          ["Warranty Expiry",   warr.warranty_expiry_date || ""],
                        ].map(([label, val]) => (
                          <tr key={label}>
                            <td style={{ fontWeight: 700, paddingBottom: 5, whiteSpace: "nowrap", width: 148, verticalAlign: "bottom" }}>{label}:</td>
                            <td style={{ borderBottom: "1px solid #000", paddingBottom: 2, paddingLeft: 4, verticalAlign: "bottom" }}>{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: "50%", verticalAlign: "top", paddingLeft: 12, borderLeft: "1px solid #ccc" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        {[
                          ["Customer Name",    warr.customer_name || unit.customer || ""],
                          ["Address",          warr.customer_address || ""],
                          ["City",             warr.customer_city || ""],
                          ["Province / State", warr.customer_province || ""],
                          ["Postal / Zip",     warr.customer_postal || ""],
                          ["Phone",            warr.customer_phone || ""],
                          ["Email",            warr.customer_email || ""],
                          ["Dealer",           warr.dealer_name || "Superior North America"],
                          ["Dealer Contact",   warr.dealer_contact || ""],
                        ].map(([label, val]) => (
                          <tr key={label}>
                            <td style={{ fontWeight: 700, paddingBottom: 5, whiteSpace: "nowrap", width: 148, verticalAlign: "bottom" }}>{label}:</td>
                            <td style={{ borderBottom: "1px solid #000", paddingBottom: 2, paddingLeft: 4, verticalAlign: "bottom" }}>{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Notes */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: "8.5pt", marginBottom: 4 }}>Notes:</div>
              <div style={{ border: "1px solid #aaa", minHeight: 48, padding: "4px 8px", fontSize: "8.5pt" }}>
                {warr.notes || ""}
              </div>
            </div>

            {/* Sign-off row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 20 }}>
              {[
                ["Completed By (Warranty Dept.)", warr.completed_by || ""],
                ["Date Completed",               warr.completed_at ? fmtDate(warr.completed_at) : ""],
                ["Engineering / QC Sign-Off",   session.engineering_initials || ""],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: "7.5pt", color: "#555", marginBottom: 2 }}>{label}</div>
                  <div style={{ borderBottom: "1.5px solid #000", minHeight: 22, paddingBottom: 2, fontSize: "9pt" }}>{val}</div>
                </div>
              ))}
            </div>

            {/* SCANNED / IN SERVICE checkboxes (for internal routing) */}
            <div style={{ marginTop: 20, display: "flex", gap: 32, fontSize: "8pt" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 48, height: 16, border: "1.5px solid #000" }} />
                <span>SCANNED (Eng dept)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 48, height: 16, border: "1.5px solid #000" }} />
                <span>IN SERVICE REGISTRATION (warranty dept)</span>
              </div>
            </div>
          </div>

          <div className="interior-footer">
            <span>DO NOT REMOVE FROM VEHICLE until warranty is registered</span>
            <span>Page {1 + defPages.length + sections.length + 1} of {1 + defPages.length + sections.length + 1}</span>
          </div>
        </div>

      </div>
    </>
  );
}
